/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import * as React from 'react';
import Link from 'next/link';

import cx from 'classnames';

import { Heading } from 'components/Heading';
import CloseIcon from 'components/Icons/CloseV2.svg';

import { useAppContext } from 'hooks/context/useAppContext';
import { useNavigation } from 'hooks/navigation/useNavigation';

import GGLogo from '../../../../../public/assets/logos/GG_Logo_Black.svg';
import MenuIcon from '../../../../../public/assets/themes/canvas/img/menu-icon.svg';
import PoweredBy from '../../../../../public/assets/themes/canvas/img/poweredby.svg';
import { CanvasAnimatedTile } from '../CanvasAnimatedTile';
// import { CanvasNavItemHome } from './CanvasNavItemHome';
import { CanvasNavItem } from './CanvasNavItem';

// import { SocialMediaLinks } from 'themes/common/components/NavItemSocial/SocialMediaLinks';
import s from './Navigation.module.css';

const CanvasNavigation = () => {
  const {
    isMenuOpen,
    showTeam,
    showPortfolio,
    showShopLink,
    aboutUrl,
    teamUrl,
    servicesUrl,
    portfolioUrl,
    contactUrl,
    bookUrl,
    isFloating,
    floatingNavRef,
    // termsOfUseUrl,
    // privacyPolicyUrl,
    packagesUrl,
    shopUrl,
    renderOverlay,
    renderMenuButton,
    tracking,
    renderMenuContainer,
    renderCloseButton,
    navigationRef,
    // socialMediaUrls,
  } = useNavigation({
    styles: {
      overlay: s.overlay,
      overlayOpen: s.overlayOpen,
      overlayClosed: s.overlayClosed,
      menuButton: s.button,
      menuContainer: s.menuContainer,
      menuContainerOpen: s.menuContainerOpen,
      menuContainerClosed: s.menuContainerClosed,
    },
  });

  const { publicUser } = useAppContext();

  return (
    <nav className={s.root} ref={navigationRef} aria-label="main navigation">
      <div className={s.topPlaceholder} ref={floatingNavRef} id="top-of-nav" />

      <div
        id="nav-bar"
        data-testid="nav-bar"
        className={cx(s.container, { [s.isFloating]: isFloating })}
      >
        <div className={s.title}>
          <Link href="/">{publicUser.business_name.toUpperCase()}</Link>
        </div>

        {renderOverlay()}
        <CanvasAnimatedTile className="navContainer">
          <div className={s.desktopFlexContainer}>
            <Link className={s.desktopTitle} href="/" passHref>
              <Heading as="h1" className={s.navBusinessName}>
                {publicUser.business_name.toUpperCase()}
              </Heading>
            </Link>
            <ul className={cx(s.desktopNav, s.menu)}>
              <CanvasNavItem to={servicesUrl}>Services</CanvasNavItem>
              <CanvasNavItem to={aboutUrl}>About</CanvasNavItem>
              {showTeam && (
                <CanvasNavItem to={teamUrl}>
                  <span data-testid="our-team">
                    <span className={s.fullText}>Team</span>
                    <span className={s.minimalText}>Team</span>
                  </span>
                </CanvasNavItem>
              )}
              {showPortfolio && (
                <CanvasNavItem to={portfolioUrl}>Portfolio</CanvasNavItem>
              )}
              {packagesUrl && (
                <CanvasNavItem
                  onClick={() => tracking.navItemClicked('packages')}
                  to={packagesUrl}
                >
                  Packages
                </CanvasNavItem>
              )}

              <CanvasNavItem to={contactUrl}>Contact</CanvasNavItem>
              <CanvasNavItem className={s.bookNowCTA} to={bookUrl}>
                <div data-testid="book-now">
                  <span className={s.fullText}>Book Now</span>
                  <span className={s.minimalText}>Book</span>
                </div>
              </CanvasNavItem>
              {showShopLink && (
                <CanvasNavItem newTab to={shopUrl}>
                  <span className={s.fullText}>Shop Now</span>
                  <span className={s.minimalText}>Shop</span>
                </CanvasNavItem>
              )}

              {/* <SocialMediaLinks
          className={cx(s.navItem, s.iconLinks)}
          urls={socialMediaUrls}
        /> */}
            </ul>
          </div>
        </CanvasAnimatedTile>
        {renderMenuButton({
          content: (
            <MenuIcon
              className={cx(s.menuIcon, isMenuOpen ? s.hideOnOpenMobile : '')}
            />
          ),
        })}
        {renderMenuContainer({
          isOpen: isMenuOpen,
          content: (
            <>
              <div className={s.headerContent}>
                <Heading as="h2">
                  {publicUser?.business_name.toUpperCase()}
                </Heading>
                <div className={s.closeButtonContainer}>
                  {renderCloseButton({
                    content: <CloseIcon className={s.closeIcon} />,
                  })}
                </div>
              </div>
              {/* <div className={s.menuHeading}>
              <CanvasNavItemHome />
            </div> */}
              <ul className={cx(s.mobileNav, s.menu)}>
                <CanvasNavItem
                  onClick={() => tracking.navItemClicked('book')}
                  to={bookUrl}
                >
                  <div data-testid="book-now" className={s.bookNowCTA}>
                    <span className={s.fullText}>Book Now</span>
                    <span className={s.minimalText}>Book Now</span>
                  </div>
                </CanvasNavItem>
                <CanvasNavItem
                  onClick={() => tracking.navItemClicked('about')}
                  className={s.aboutPage}
                  to={aboutUrl}
                >
                  About
                </CanvasNavItem>
                {showTeam && (
                  <CanvasNavItem
                    onClick={() => tracking.navItemClicked('team')}
                    className={s.teamPage}
                    to={teamUrl}
                  >
                    <span data-testid="our-team">
                      <span className={s.fullText}>Our Team</span>
                      <span className={s.minimalText}>Team</span>
                    </span>
                  </CanvasNavItem>
                )}
                <CanvasNavItem
                  onClick={() => tracking.navItemClicked('services')}
                  className={cx(
                    !showTeam ? s.servicesWithNoTeam : s.servicesPage,
                  )}
                  to={servicesUrl}
                >
                  Services
                </CanvasNavItem>

                {showPortfolio && (
                  <CanvasNavItem
                    onClick={() => tracking.navItemClicked('portfolio')}
                    className={s.portfolioPage}
                    to={portfolioUrl}
                  >
                    Portfolio
                  </CanvasNavItem>
                )}

                {packagesUrl && (
                  <CanvasNavItem
                    onClick={() => tracking.navItemClicked('packages')}
                    to={packagesUrl}
                  >
                    Packages
                  </CanvasNavItem>
                )}

                <CanvasNavItem
                  onClick={() => tracking.navItemClicked('contact')}
                  className={cx(
                    showPortfolio
                      ? s.contactsPagePortfolioPersist
                      : s.contactsPage,
                  )}
                  to={contactUrl}
                >
                  Contact
                </CanvasNavItem>
                {showShopLink && (
                  <CanvasNavItem
                    onClick={() => tracking.navItemClicked('shop')}
                    className={s.shopNowCTA}
                    newTab
                    to={shopUrl}
                  >
                    <span className={s.fullText}>Shop Now</span>
                    <span className={s.minimalText}>Shop</span>
                  </CanvasNavItem>
                )}
              </ul>
              <div className={cx(s.copyright)}>
                <div>
                  <a
                    href="https://glossgenius.com"
                    className={s.logo}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PoweredBy
                      className={s.poweredBy}
                      width="125"
                      height="13"
                    />
                    <GGLogo className={s.ggLogoSVG} width="125" height="13" />
                  </a>
                </div>
              </div>
            </>
          ),
        })}
      </div>
    </nav>
  );
};

export { CanvasNavigation };
